// src/components/layout/Footer.tsx
// Four-column footer: brand · quick links · contact · dev credit.
// Bottom bar: copyright + legal modal triggers.
// Tagline is editable from Firestore (siteContent/tagline).
// Fully dark/light-mode compatible via useThemePalette().

import React, { useEffect, useRef, useState } from 'react';
import { Link }                from 'react-router-dom';
import { doc, getDoc }         from 'firebase/firestore';
import { db }                  from '../../lib/firebase';
import { useThemePalette }     from '../../hooks/useThemePalette';
import { useThemeStore }       from '../../stores/useThemeStore';
import { useLegalModalStore }  from '../../stores/useLegalModalStore';
import { useT }                from '../../data/translations';
import { usePaymentStore }     from '../../stores/usePaymentStore';

// ── Keyframe injection ────────────────────────────────────────────────────────
(function injectFooterStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-footer-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes footerReveal {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes footerLinkHover {
      from { width: 0; }
      to   { width: 100%; }
    }
    .esp-footer-link {
      position: relative;
      display: inline-block;
      text-decoration: none;
      transition: color 0.2s ease;
    }
    .esp-footer-link::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background: currentColor;
      transition: width 0.25s ease;
    }
    .esp-footer-link:hover::after { width: 100%; }
    .esp-footer-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 10px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      transition: transform 0.18s ease, opacity 0.18s ease;
      text-decoration: none;
    }
    .esp-footer-badge:hover { transform: translateY(-2px); opacity: 0.85; }
    .esp-social-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 8px;
      border: 1px solid;
      cursor: pointer;
      transition: transform 0.18s ease, background 0.18s ease, opacity 0.18s ease;
      text-decoration: none;
      flex-shrink: 0;
    }
    .esp-social-btn:hover { transform: translateY(-2px) scale(1.08); }
    .esp-footer-divider {
      width: 100%;
      height: 1px;
    }
    @media (prefers-reduced-motion: reduce) {
      .esp-footer-link::after, .esp-footer-badge, .esp-social-btn {
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(s);
})();

// ── Helpers ───────────────────────────────────────────────────────────────────
function FooterHeading({ label, C }: { label: string; C: ReturnType<typeof useThemePalette> }) {
  return (
    <h3 style={{
      fontSize:      '0.7rem',
      fontWeight:    800,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color:         C.accent,
      margin:        '0 0 20px 0',
    }}>
      {label}
    </h3>
  );
}

function FooterNavLink({
  href,
  to,
  children,
  C,
  onClick,
}: {
  href?:     string;
  to?:       string;
  children:  React.ReactNode;
  C:         ReturnType<typeof useThemePalette>;
  onClick?:  () => void;
}) {
  const style: React.CSSProperties = {
    color:      C.muted,
    fontSize:   '0.88rem',
    fontWeight: 500,
    lineHeight: '1',
  };

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="esp-footer-link"
        style={{
          ...style,
          background: 'none',
          border:     'none',
          padding:    '0',
          cursor:     'pointer',
          fontFamily: 'inherit',
          textAlign:  'left',
        }}
      >
        {children}
      </button>
    );
  }

  if (to) {
    return (
      <Link to={to} className="esp-footer-link" style={style}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className="esp-footer-link" style={style} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

// ── Tech stack badge ──────────────────────────────────────────────────────────
function TechBadge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="esp-footer-badge"
      style={{ color, background: bg, border: `1px solid ${color}33` }}
    >
      {label}
    </span>
  );
}

// ── Social icon button ────────────────────────────────────────────────────────
function SocialBtn({
  href,
  label,
  C,
  children,
}: {
  href:     string;
  label:    string;
  C:        ReturnType<typeof useThemePalette>;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="esp-social-btn"
      style={{
        borderColor: C.border,
        color:       C.muted,
        background:  C.card,
      }}
    >
      {children}
    </a>
  );
}

// ── Main Footer ───────────────────────────────────────────────────────────────
export function Footer() {
  const C        = useThemePalette();
  const isDark   = useThemeStore((s) => s.theme) === 'dark';
  const openLegal = useLegalModalStore((s) => s.open);
  const t         = useT();

  // Firestore-backed tagline
  const [tagline, setTagline] = useState<string>(
    'Sports-focused English for athletes, coaches, and professionals who want to communicate with confidence on the world stage.'
  );

  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'tagline'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.text) setTagline(data.text as string);
        }
      })
      .catch(() => {/* silently fall back to default */});
  }, []);

  // Responsive: track viewport width
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTablet, setIsTablet] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth < 1024);
    }
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection observer for reveal animation
  const footerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const revealStyle: React.CSSProperties = {
    animation:       visible ? 'footerReveal 0.55s ease both' : 'none',
    opacity:         visible ? undefined : 0,
  };

  // Grid layout
  const gridStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '40px' }
    : isTablet
    ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px 32px' }
    : { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '32px' };

  const openPayment = usePaymentStore((s) => s.openModal);

  return (
    <footer
      ref={footerRef}
      style={{
        borderTop:  `1px solid ${C.border}`,
        background: isDark
          ? 'linear-gradient(180deg, rgba(8,12,20,0) 0%, rgba(8,12,20,0.97) 15%)'
          : 'linear-gradient(180deg, rgba(248,250,252,0) 0%, rgba(248,250,252,0.97) 15%)',
        backgroundColor: isDark ? '#080c14' : '#f8fafc',
        position:        'relative',
        overflow:        'hidden',
      }}
      role="contentinfo"
    >
      {/* Subtle background glow */}
      <div
        aria-hidden="true"
        style={{
          position:     'absolute',
          inset:        0,
          pointerEvents: 'none',
          overflow:     'hidden',
        }}
      >
        <div style={{
          position:     'absolute',
          top:          '-120px',
          left:         '50%',
          transform:    'translateX(-50%)',
          width:        '700px',
          height:       '300px',
          borderRadius: '50%',
          background:   isDark
            ? 'radial-gradient(ellipse, rgba(79,140,247,0.06) 0%, transparent 70%)'
            : 'radial-gradient(ellipse, rgba(79,140,247,0.05) 0%, transparent 70%)',
          filter:       'blur(40px)',
        }} />
      </div>

      {/* ── Main grid ── */}
      <div
        style={{
          ...revealStyle,
          maxWidth: '1280px',
          margin:   '0 auto',
          padding:  isMobile ? '48px 24px 36px' : '64px 48px 48px',
          position: 'relative',
        }}
      >
        <div style={gridStyle}>

          {/* ── Column 1: Brand ─────────────────────────────────────── */}
          <div>
            {/* Logo wordmark */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{
                fontSize:      '1.6rem',
                fontWeight:    900,
                letterSpacing: '-0.04em',
                background:    `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor:  'transparent',
                backgroundClip:       'text',
              }}>
                START
              </span>
            </div>

            {/* Tagline — Firestore-editable */}
            <p style={{
              fontSize:   '0.86rem',
              color:      C.muted,
              lineHeight: '1.65',
              margin:     '0 0 24px',
              maxWidth:   '300px',
            }}>
              {tagline}
            </p>

            {/* Language / region note */}
            <div style={{
              display:      'inline-flex',
              alignItems:   'center',
              gap:          '8px',
              padding:      '6px 12px',
              borderRadius: '8px',
              border:       `1px solid ${C.border}`,
              background:   C.card,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span style={{ fontSize: '0.78rem', color: C.muted, fontWeight: 600 }}>
                English · Georgian
              </span>
            </div>
          </div>

          {/* ── Column 2: Quick Links ────────────────────────────────── */}
          <div>
            <FooterHeading label={t('FOOTER_QUICK_LINKS')} C={C} />
            <nav aria-label="Footer navigation">
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <li>
                  <FooterNavLink to="/exercise" C={C}>
                    {t('FOOTER_LINK_LEARNING')}
                  </FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/about" C={C}>
                    {t('FOOTER_LINK_ABOUT')}
                  </FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/partners" C={C}>
                    {t('FOOTER_LINK_PARTNERS')}
                  </FooterNavLink>
                </li>
                <li>
                  <FooterNavLink
                    C={C}
                    onClick={openPayment}
                  >
                    {t('FOOTER_LINK_PREMIUM')}
                  </FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/contact" C={C}>
                    {t('FOOTER_LINK_CONTACT')}
                  </FooterNavLink>
                </li>
              </ul>
            </nav>
          </div>

          {/* ── Column 3: Contact Info ───────────────────────────────── */}
          <div>
            <FooterHeading label={t('FOOTER_CONTACT_TITLE')} C={C} />
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Email */}
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{
                  flexShrink: 0,
                  marginTop:  '1px',
                  width:      '28px',
                  height:     '28px',
                  borderRadius: '7px',
                  background:   isDark ? 'rgba(79,140,247,0.1)' : 'rgba(79,140,247,0.08)',
                  border:       `1px solid ${C.accent}33`,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Email</p>
                  <a href="mailto:info@startlearning.ge" className="esp-footer-link" style={{ fontSize: '0.86rem', color: C.text, fontWeight: 500 }}>
                    info@startlearning.ge
                  </a>
                </div>
              </li>

              {/* Phone */}
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{
                  flexShrink: 0,
                  marginTop:  '1px',
                  width:      '28px',
                  height:     '28px',
                  borderRadius: '7px',
                  background:   isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.08)',
                  border:       `1px solid ${C.accent2}33`,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.accent2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.49 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.4 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l1.19-1.74a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Phone</p>
                  <a href="tel:+995599000000" className="esp-footer-link" style={{ fontSize: '0.86rem', color: C.text, fontWeight: 500 }}>
                    +995 599 000 000
                  </a>
                </div>
              </li>

              {/* Location */}
              <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{
                  flexShrink: 0,
                  marginTop:  '1px',
                  width:      '28px',
                  height:     '28px',
                  borderRadius: '7px',
                  background:   isDark ? 'rgba(168,85,247,0.1)' : 'rgba(168,85,247,0.08)',
                  border:       `1px solid ${C.accent3}33`,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.accent3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Location</p>
                  <span style={{ fontSize: '0.86rem', color: C.text, fontWeight: 500 }}>
                    Tbilisi, Georgia
                  </span>
                </div>
              </li>

            </ul>
          </div>

          {/* ── Column 4: Developer Credit ───────────────────────────── */}
          <div>
            <FooterHeading label={t('FOOTER_DEV_TITLE')} C={C} />

            {/* Dev card */}
            <div style={{
              padding:      '16px',
              borderRadius: '12px',
              border:       `1px solid ${C.border}`,
              background:   C.card,
              marginBottom: '16px',
            }}>
              {/* Avatar + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width:        '40px',
                  height:       '40px',
                  borderRadius: '10px',
                  background:   `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 50%, ${C.accent3} 100%)`,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                  flexShrink:   0,
                  fontWeight:   800,
                  fontSize:     '1rem',
                  color:        '#fff',
                  letterSpacing: '-0.02em',
                }}>
                  NT
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: C.text }}>
                    Nikoloz Todua
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: C.muted, fontWeight: 500 }}>
                    Full-Stack Developer
                  </p>
                </div>
              </div>

              {/* Tech stack badges */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                <TechBadge label="React"       color="#61dafb" bg={isDark ? 'rgba(97,218,251,0.08)' : 'rgba(97,218,251,0.1)'} />
                <TechBadge label="TypeScript"  color="#3178c6" bg={isDark ? 'rgba(49,120,198,0.1)'  : 'rgba(49,120,198,0.08)'} />
                <TechBadge label="Firebase"    color="#ffa000" bg={isDark ? 'rgba(255,160,0,0.09)'  : 'rgba(255,160,0,0.08)'} />
                <TechBadge label="Vite"        color="#a259ff" bg={isDark ? 'rgba(162,89,255,0.1)'  : 'rgba(162,89,255,0.08)'} />
              </div>

              {/* Social links */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <SocialBtn href="https://github.com/nikoloztodua" label="GitHub" C={C}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.165 6.839 9.489.5.09.682-.218.682-.484 0-.236-.009-.866-.014-1.699-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                </SocialBtn>
                <SocialBtn href="https://linkedin.com/in/nikoloztodua" label="LinkedIn" C={C}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                </SocialBtn>
                <SocialBtn href="mailto:nikoloz@startlearning.ge" label="Email Nikoloz" C={C}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </SocialBtn>
              </div>
            </div>
          </div>

        </div>

        {/* ── Divider ── */}
        <div
          className="esp-footer-divider"
          style={{
            background: isDark
              ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.07) 20%, rgba(255,255,255,0.07) 80%, transparent)'
              : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.07) 20%, rgba(0,0,0,0.07) 80%, transparent)',
            margin: '40px 0 28px',
          }}
        />

        {/* ── Bottom bar ── */}
        <div style={{
          display:        'flex',
          flexDirection:  isMobile ? 'column' : 'row',
          alignItems:     isMobile ? 'center' : 'center',
          justifyContent: 'space-between',
          gap:            '16px',
          textAlign:      isMobile ? 'center' : 'left',
        }}>

          {/* Copyright */}
          <p style={{
            margin:     0,
            fontSize:   '0.8rem',
            color:      C.muted,
            fontWeight: 500,
          }}>
            {t('FOOTER_COPYRIGHT')}
          </p>

          {/* Legal links */}
          <nav aria-label="Legal links">
            <ul style={{
              listStyle: 'none',
              margin:    0,
              padding:   0,
              display:   'flex',
              flexWrap:  'wrap',
              gap:       isMobile ? '12px 20px' : '0',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}>
              {[
                { key: 'privacy',       label: t('FOOTER_LEGAL_PRIVACY')       },
                { key: 'cookies',       label: t('FOOTER_LEGAL_COOKIES')       },
                { key: 'terms',         label: t('FOOTER_LEGAL_TERMS')         },
                { key: 'accessibility', label: t('FOOTER_LEGAL_ACCESSIBILITY') },
              ].map(({ key, label }, i) => (
                <li
                  key={key}
                  style={{
                    display:    'flex',
                    alignItems: 'center',
                    gap:        isMobile ? 0 : '0',
                  }}
                >
                  {!isMobile && i > 0 && (
                    <span style={{ color: C.border, margin: '0 12px', userSelect: 'none' }}>·</span>
                  )}
                  <button
                    onClick={() => openLegal(key as any)}
                    style={{
                      background:  'none',
                      border:      'none',
                      padding:     0,
                      cursor:      'pointer',
                      fontFamily:  'inherit',
                      fontSize:    '0.8rem',
                      color:       C.muted,
                      fontWeight:  500,
                      textDecoration: 'none',
                      transition:  'color 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = C.text)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}